import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { HttpCommonService } from '@shared/services/http-common.service';
import { GenericHttpResponse } from '@shared/services/interfaces/generic-http-response.interface';

import { Cliente } from '../../crm/clientes/clientes.interface';
import {
  AnimalResumen,
  ClienteComboItem,
  OpsColor,
  OpsPunto,
  PajuelaOpsPayload,
  ParamResumen,
  SalidaStockPayload,
  StockVitrificado,
  StockVitrificadoPayload,
  StockVitrificadoQueryParams,
} from './embriones.interface';

@Injectable({
  providedIn: 'root',
})
export class EmbrionesService {
  private http = inject(HttpClient);
  private httpCommonService = inject(HttpCommonService);

  getStockVitrificados(params?: StockVitrificadoQueryParams): Observable<GenericHttpResponse<StockVitrificado[]>> {
    const url = this.httpCommonService.makeUrl('/sto/stocksVitrificados', params);

    return this.http.get<GenericHttpResponse<StockVitrificado[]>>(url);
  }

  getStockVitrificado(id: number): Observable<GenericHttpResponse<StockVitrificado>> {
    const url = this.httpCommonService.makeUrl(`/sto/stocksVitrificados/${id}`);

    return this.http.get<GenericHttpResponse<StockVitrificado>>(url);
  }

  createStockVitrificado(payload: StockVitrificadoPayload): Observable<GenericHttpResponse<StockVitrificado>> {
    const url = this.httpCommonService.makeUrl('/sto/stocksVitrificados');

    return this.http.post<GenericHttpResponse<StockVitrificado>>(url, payload);
  }

  updateStockVitrificado(id: number, payload: StockVitrificadoPayload): Observable<GenericHttpResponse<StockVitrificado>> {
    const url = this.httpCommonService.makeUrl(`/sto/stocksVitrificados/${id}`);

    return this.http.put<GenericHttpResponse<StockVitrificado>>(url, payload);
  }

  deleteStockVitrificado(id: number): Observable<GenericHttpResponse<void>> {
    const url = this.httpCommonService.makeUrl(`/sto/stocksVitrificados/${id}`);

    return this.http.delete<GenericHttpResponse<void>>(url);
  }

  /**
   * Registra una salida que no pasa por transferencia (donación, venta o pérdida).
   * Devuelve el stock actualizado, así el detalle se refresca sin volver a pedirlo.
   */
  registrarSalidaStock(stockId: number, payload: SalidaStockPayload): Observable<GenericHttpResponse<StockVitrificado>> {
    const url = this.httpCommonService.makeUrl(`/sto/stocksVitrificados/${stockId}/salidas`);

    return this.http.post<GenericHttpResponse<StockVitrificado>>(url, payload);
  }

  anularSalidaStock(stockId: number, salidaId: number): Observable<GenericHttpResponse<StockVitrificado>> {
    const url = this.httpCommonService.makeUrl(`/sto/stocksVitrificados/${stockId}/salidas/${salidaId}`);

    return this.http.delete<GenericHttpResponse<StockVitrificado>>(url);
  }

  /** Clientes disponibles como destino de una donación o venta. */
  getClientesDestino(): Observable<GenericHttpResponse<ClienteComboItem[]>> {
    const url = this.httpCommonService.makeUrl('/combo/clientes');

    return this.http.get<GenericHttpResponse<ClienteComboItem[]>>(url);
  }

  comboClientes(): Observable<GenericHttpResponse<Cliente[]>> {
    const url = this.httpCommonService.makeUrl('/labs/agendamientos-opu/clientes');

    return this.http.get<GenericHttpResponse<Cliente[]>>(url);
  }

  /**
   * `/anim/animales` responde paginado (`{ data: [...], pagination: {...} }`) y con
   * 15 registros por defecto, así que hay que desenvolver la respuesta y pedir
   * una página grande para que el combo no quede recortado.
   */
  getAnimalesByCliente(clienteId: number, tipo?: string): Observable<GenericHttpResponse<AnimalResumen[]>> {
    const params: Record<string, unknown> = { cli_cliente_id: clienteId, per_page: 100 };

    if (tipo) {
      params['tipo'] = tipo;
    }

    const url = this.httpCommonService.makeUrl('/anim/animales', params);

    return this.http.get<GenericHttpResponse<AnimalResumen[] | { data: AnimalResumen[] }>>(url).pipe(
      map(response => ({
        ...response,
        data: Array.isArray(response.data) ? response.data : response.data.data,
      })),
    );
  }

  getDonantes(clienteId: number): Observable<GenericHttpResponse<AnimalResumen[]>> {
    return this.getAnimalesByCliente(clienteId, 'V');
  }

  /** El toro es un catálogo global: el combo no filtra por cliente. */
  getToros(): Observable<GenericHttpResponse<AnimalResumen[]>> {
    const url = this.httpCommonService.makeUrl('/anim/toros');

    return this.http.get<GenericHttpResponse<AnimalResumen[] | { data: AnimalResumen[] }>>(url).pipe(
      map(response => ({
        ...response,
        data: Array.isArray(response.data) ? response.data : response.data.data,
      })),
    );
  }

  getRaques(): Observable<GenericHttpResponse<ParamResumen[]>> {
    const url = this.httpCommonService.makeUrl('/combo/raques');

    return this.http.get<GenericHttpResponse<ParamResumen[]>>(url);
  }

  getOpsPuntos(): Observable<GenericHttpResponse<OpsPunto[]>> {
    const url = this.httpCommonService.makeUrl('/combo/ops-puntos');

    return this.http.get<GenericHttpResponse<OpsPunto[]>>(url);
  }

  getOpsColores(): Observable<GenericHttpResponse<OpsColor[]>> {
    const url = this.httpCommonService.makeUrl('/combo/ops-colores');

    return this.http.get<GenericHttpResponse<OpsColor[]>>(url);
  }

  /** El OPS marca cada pajuela, así que se corrige por pajuela y no por lote. */
  actualizarPajuela(
    stockId: number,
    pajuelaId: number,
    body: PajuelaOpsPayload,
  ): Observable<GenericHttpResponse<StockVitrificado>> {
    const url = this.httpCommonService.makeUrl(`/sto/stocksVitrificados/${stockId}/pajuelas/${pajuelaId}`);

    return this.http.put<GenericHttpResponse<StockVitrificado>>(url, body);
  }
}
